<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import PipelineAPI from 'dashboard/api/pipeline';
import pipelineBoardsAPI from 'dashboard/api/pipelineBoards';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import CardPriorityIcon from 'dashboard/components-next/Conversation/ConversationCard/CardPriorityIcon.vue';
import CreateBoardDialog from './CreateBoardDialog.vue';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';

const PIPELINE_STAGE_KEY = 'pipeline_stage';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const { accountScopedRoute } = useAccount();

const stages = ref([]);
const unassignedCount = ref(0);
const isFetching = ref(false);
const isNotConfigured = ref(false);
const movingId = ref(null);
const searchQuery = ref('');
const selectedInboxId = ref('');
const selectedStatus = ref('open');

// Boards mode (new tables). Falls back to the legacy attribute board.
const boards = ref([]);
const selectedBoardId = ref(null);
const isBoardsMode = computed(() => boards.value.length > 0);
const createBoardDialogRef = ref(null);
const isCreatingBoard = ref(false);

const inboxes = useMapGetter('inboxes/getInboxes');
const inboxById = useMapGetter('inboxes/getInboxById');

const boardParams = computed(() => {
  const params = {};
  if (selectedInboxId.value) params.inbox_id = selectedInboxId.value;
  if (selectedStatus.value && selectedStatus.value !== 'all') {
    params.status = selectedStatus.value;
  }
  if (searchQuery.value) params.q = searchQuery.value;
  return params;
});

const totalCards = computed(() =>
  stages.value.reduce(
    (sum, stage) => sum + (stage.count ?? stage.conversations?.length ?? 0),
    0
  )
);

const fetchBoards = async () => {
  try {
    const { data } = await pipelineBoardsAPI.getBoards();
    boards.value = Array.isArray(data) ? data : [];
    if (isBoardsMode.value && !selectedBoardId.value) {
      selectedBoardId.value = boards.value[0].id;
    }
  } catch (error) {
    if (error?.response?.status === 403) {
      useAlert(t('PIPELINE.BOARD.LOAD_ERROR'));
      stages.value = [];
    }
    boards.value = [];
  }
};

const fetchBoardDetail = async () => {
  isFetching.value = true;
  isNotConfigured.value = false;
  try {
    const { data } = await pipelineBoardsAPI.getBoard(
      selectedBoardId.value,
      boardParams.value
    );
    stages.value = data.stages || [];
    unassignedCount.value = 0;
  } catch (error) {
    if (error?.response?.status === 404) {
      isNotConfigured.value = true;
      stages.value = [];
    } else {
      useAlert(t('PIPELINE.BOARD.LOAD_ERROR'));
      stages.value = [];
    }
  } finally {
    isFetching.value = false;
  }
};

const fetchBoard = async () => {
  isFetching.value = true;
  isNotConfigured.value = false;
  try {
    const { data } = await PipelineAPI.getBoard(boardParams.value);
    stages.value = data.stages || [];
    unassignedCount.value = data.unassigned_count || 0;
  } catch (error) {
    if (error?.response?.status === 404) {
      isNotConfigured.value = true;
      stages.value = [];
    } else {
      useAlert(t('PIPELINE.BOARD.LOAD_ERROR'));
      stages.value = [];
    }
  } finally {
    isFetching.value = false;
  }
};

const refresh = () => {
  if (isBoardsMode.value && selectedBoardId.value) return fetchBoardDetail();
  return fetchBoard();
};

const openCreateBoard = () => createBoardDialogRef.value?.open();

const createBoard = async board => {
  isCreatingBoard.value = true;
  try {
    const { data } = board.template
      ? await pipelineBoardsAPI.createBoardFromTemplate(board)
      : await pipelineBoardsAPI.createBoard(board);
    createBoardDialogRef.value?.onSuccess();
    await fetchBoards();
    // the selectedBoardId watcher below loads the stages of the new board
    selectedBoardId.value = data.id;
    useAlert(t('PIPELINE.CREATE_BOARD.SUCCESS'));
  } catch (error) {
    useAlert(t('PIPELINE.CREATE_BOARD.ERROR'));
  } finally {
    isCreatingBoard.value = false;
  }
};

const openConversation = conversation => {
  router.push(
    accountScopedRoute('inbox_conversation', {
      conversation_id: conversation.id,
    })
  );
};

const lastMessagePreview = conversation => {
  const messages = conversation.messages || [];
  return messages[0]?.content?.slice(0, 120) || t('PIPELINE.CARD.NO_PREVIEW');
};

const formatActivityTime = timestamp => {
  return timestamp ? shortTimestamp(dynamicTime(timestamp)) : '';
};

const conversationInbox = conversation => {
  return inboxById.value(conversation.inbox_id) || {};
};

const conversationAssignee = conversation => {
  return conversation.meta?.assignee || {};
};

const statusLabel = status => {
  if (status === 'pending') return t('PIPELINE.CARD.STATUS_PENDING');
  if (status === 'resolved') return t('PIPELINE.CARD.STATUS_RESOLVED');
  if (status === 'snoozed') return t('PIPELINE.CARD.STATUS_SNOOZED');
  return t('PIPELINE.CARD.STATUS_OPEN');
};

const adjustStageCount = (stageName, delta) => {
  const stage = stages.value.find(item => item.name === stageName);
  if (stage) stage.count = Math.max(0, stage.count + delta);
};

const onLegacyDragChange = async (event, targetStage) => {
  if (event.removed) {
    adjustStageCount(targetStage, -1);
    return;
  }

  const added = event.added;
  if (!added) return;

  const conversation = added.element;

  movingId.value = conversation.id;
  try {
    await PipelineAPI.moveCard(conversation.id, targetStage);
    conversation.custom_attributes = {
      ...(conversation.custom_attributes || {}),
      [PIPELINE_STAGE_KEY]: targetStage,
    };
    adjustStageCount(targetStage, 1);
  } catch (error) {
    useAlert(t('PIPELINE.BOARD.MOVE_ERROR'));
    await fetchBoard();
  } finally {
    movingId.value = null;
  }
};

const onBoardsDragChange = async (event, targetStage) => {
  const added = event.added;
  if (!added) return;

  const conversation = added.element;
  const cardId = conversation.card_id;
  if (!cardId) {
    await fetchBoardDetail();
    return;
  }

  movingId.value = conversation.id;
  try {
    await pipelineBoardsAPI.moveBoardCard(
      selectedBoardId.value,
      cardId,
      targetStage.id,
      added.newIndex
    );
    await fetchBoardDetail();
  } catch (error) {
    useAlert(t('PIPELINE.BOARD.MOVE_ERROR'));
    await fetchBoardDetail();
  } finally {
    movingId.value = null;
  }
};

const onDragChange = (event, targetStage) => {
  if (isBoardsMode.value) return onBoardsDragChange(event, targetStage);
  return onLegacyDragChange(event, targetStage.name);
};

const goToAttributes = () => {
  router.push(accountScopedRoute('attributes_list'));
};

let searchTimeout = null;
const onSearchInput = () => {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(refresh, 400);
};

let realtimeTimeout = null;
const onBoardUpdatedRemotely = data => {
  if (!isBoardsMode.value) return;
  if (Number(data?.board_id) !== Number(selectedBoardId.value)) return;
  clearTimeout(realtimeTimeout);
  realtimeTimeout = setTimeout(fetchBoardDetail, 800);
};

onMounted(async () => {
  await Promise.all([
    store.dispatch('inboxes/get'),
    store.dispatch('agents/get'),
  ]);
  await fetchBoards();
  await refresh();
  emitter.on(BUS_EVENTS.PIPELINE_BOARD_UPDATED, onBoardUpdatedRemotely);
});

onUnmounted(() => {
  emitter.off(BUS_EVENTS.PIPELINE_BOARD_UPDATED, onBoardUpdatedRemotely);
  clearTimeout(searchTimeout);
  clearTimeout(realtimeTimeout);
});

watch([selectedInboxId, selectedStatus], refresh);
watch(selectedBoardId, () => {
  if (isBoardsMode.value) fetchBoardDetail();
});
watch(
  () => route.params.accountId,
  () => refresh()
);
</script>

<template>
  <div class="flex flex-col w-full h-full min-w-0 bg-n-background">
    <div
      class="flex flex-col gap-2 px-4 py-3 border-b border-n-weak lg:flex-row lg:items-center lg:justify-between"
    >
      <div class="flex items-center min-w-0 gap-2">
        <h1
          class="text-base font-medium me-1 text-n-slate-12 whitespace-nowrap shrink-0"
        >
          {{ t('PIPELINE.BOARD.TITLE') }}
        </h1>
        <span
          v-if="!isFetching && !isNotConfigured"
          class="text-sm rounded-md px-2 py-0.5 bg-n-alpha-2 text-n-slate-11 shrink-0"
        >
          {{ totalCards }}
        </span>
        <select
          v-if="isBoardsMode"
          v-model="selectedBoardId"
          :aria-label="t('PIPELINE.BOARD.SELECT_BOARD')"
          class="h-9 w-40 shrink-0 rounded-lg border border-n-weak bg-n-solid-1 px-2 text-sm text-n-slate-12"
        >
          <option v-for="board in boards" :key="board.id" :value="board.id">
            {{ board.name }}
          </option>
        </select>
        <Button
          v-if="!isNotConfigured"
          icon="i-lucide-plus"
          size="sm"
          color="slate"
          class="shrink-0"
          :label="t('PIPELINE.CREATE_BOARD.OPEN')"
          @click="openCreateBoard"
        />
      </div>
      <div class="flex items-center min-w-0 gap-2">
        <input
          v-model="searchQuery"
          type="search"
          :placeholder="t('PIPELINE.BOARD.SEARCH_PLACEHOLDER')"
          class="h-9 w-56 min-w-0 rounded-lg border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand xl:w-72"
          @input="onSearchInput"
        />
        <select
          v-model="selectedInboxId"
          class="h-9 w-36 shrink-0 rounded-lg border border-n-weak bg-n-solid-1 px-2 text-sm text-n-slate-12"
        >
          <option value="">{{ t('PIPELINE.BOARD.ALL_INBOXES') }}</option>
          <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
            {{ inbox.name }}
          </option>
        </select>
        <select
          v-model="selectedStatus"
          class="h-9 w-28 shrink-0 rounded-lg border border-n-weak bg-n-solid-1 px-2 text-sm text-n-slate-12"
        >
          <option value="open">{{ t('PIPELINE.BOARD.STATUS_OPEN') }}</option>
          <option value="pending">
            {{ t('PIPELINE.BOARD.STATUS_PENDING') }}
          </option>
          <option value="resolved">
            {{ t('PIPELINE.BOARD.STATUS_RESOLVED') }}
          </option>
          <option value="all">{{ t('PIPELINE.BOARD.STATUS_ALL') }}</option>
        </select>
        <Button
          icon="i-lucide-refresh-cw"
          size="sm"
          color="slate"
          class="shrink-0"
          :disabled="isFetching"
          @click="refresh"
        />
      </div>
    </div>

    <div v-if="isFetching" class="flex items-center justify-center flex-1">
      <Spinner class="text-n-brand" />
    </div>

    <div
      v-else-if="isNotConfigured"
      class="flex flex-col items-center justify-center flex-1 gap-3 px-6 text-center"
    >
      <span class="i-lucide-kanban-square text-4xl text-n-slate-9" />
      <h2 class="text-base font-medium text-n-slate-12">
        {{ t('PIPELINE.EMPTY.TITLE') }}
      </h2>
      <p class="max-w-md text-sm text-n-slate-11">
        {{ t('PIPELINE.EMPTY.DESCRIPTION') }}
      </p>
      <div class="flex items-center gap-1">
        <Button size="sm" @click="openCreateBoard">
          {{ t('PIPELINE.CREATE_BOARD.OPEN') }}
        </Button>
        <Button size="sm" variant="link" @click="goToAttributes">
          {{ t('PIPELINE.EMPTY.CTA') }}
        </Button>
      </div>
    </div>

    <div v-else class="flex flex-col flex-1 min-h-0">
      <p v-if="totalCards === 0" class="px-4 pt-3 text-sm text-n-slate-11">
        {{ t('PIPELINE.BOARD.NO_RESULTS') }}
      </p>
      <div class="flex flex-1 gap-2 p-3 overflow-x-auto min-h-0">
        <section
          v-for="stage in stages"
          :key="isBoardsMode ? stage.id : stage.name"
          class="flex flex-col w-60 shrink-0 rounded-xl border border-n-weak bg-n-solid-1 2xl:w-72"
        >
          <header
            class="flex items-center gap-2 px-3 py-2 border-b border-n-weak"
          >
            <h2 class="text-sm font-medium truncate text-n-slate-12">
              {{ stage.name }}
            </h2>
            <span
              class="text-xs rounded-md px-1.5 py-0.5 bg-n-alpha-2 text-n-slate-11"
            >
              {{ stage.count }}
            </span>
          </header>
          <Draggable
            :list="stage.conversations"
            :group="{ name: 'pipeline', pull: true, put: true }"
            item-key="id"
            animation="200"
            ghost-class="opacity-50"
            class="flex flex-col flex-1 gap-2 p-2 overflow-y-auto min-h-24"
            @change="event => onDragChange(event, stage)"
          >
            <template #item="{ element }">
              <article
                :key="element.id"
                class="p-3 rounded-lg border border-n-weak bg-n-background cursor-pointer hover:border-n-brand"
                @click="openConversation(element)"
              >
                <div class="flex items-center gap-2">
                  <span class="text-sm font-medium truncate text-n-slate-12">
                    {{ element.meta?.sender?.name || `#${element.id}` }}
                  </span>
                  <span
                    v-if="element.unread_count > 0"
                    class="text-xs font-medium rounded-full px-1.5 py-0.5 ms-auto bg-n-brand text-white"
                  >
                    {{ element.unread_count }}
                  </span>
                  <CardPriorityIcon
                    v-else-if="element.priority"
                    :priority="element.priority"
                    class="ms-auto"
                  />
                  <span
                    v-if="movingId === element.id"
                    class="text-xs text-n-slate-11"
                    :class="{
                      'ms-auto': !element.unread_count && !element.priority,
                    }"
                  >
                    {{ t('PIPELINE.CARD.MOVING') }}
                  </span>
                </div>
                <p class="mt-1 text-sm line-clamp-2 text-n-slate-11">
                  {{ lastMessagePreview(element) }}
                </p>
                <div class="flex flex-wrap items-center gap-1 mt-2">
                  <span
                    class="text-xs rounded px-1.5 py-0.5 bg-n-alpha-2 text-n-slate-11"
                  >
                    {{ statusLabel(element.status) }}
                  </span>
                  <span
                    v-for="label in element.labels || []"
                    :key="label"
                    class="text-xs rounded px-1.5 py-0.5 bg-n-alpha-2 text-n-slate-11"
                  >
                    {{ label }}
                  </span>
                  <span class="text-xs ms-auto text-n-slate-10">
                    {{ formatActivityTime(element.last_activity_at) }}
                  </span>
                </div>
                <div
                  class="flex items-center gap-1.5 mt-2 pt-2 border-t border-n-weak"
                >
                  <ChannelIcon
                    v-if="conversationInbox(element).id"
                    :inbox="conversationInbox(element)"
                    class="size-4 shrink-0 text-n-slate-11"
                  />
                  <span class="text-xs truncate text-n-slate-11">
                    {{ conversationInbox(element).name || '' }}
                  </span>
                  <span class="flex items-center gap-1 ms-auto shrink-0">
                    <Avatar
                      v-if="conversationAssignee(element).name"
                      :name="conversationAssignee(element).name"
                      :src="conversationAssignee(element).thumbnail"
                      :size="20"
                    />
                    <span class="text-xs text-n-slate-11">
                      {{
                        conversationAssignee(element).name ||
                        t('PIPELINE.CARD.UNASSIGNED')
                      }}
                    </span>
                  </span>
                </div>
              </article>
            </template>
          </Draggable>
        </section>
      </div>
    </div>

    <CreateBoardDialog
      ref="createBoardDialogRef"
      :is-loading="isCreatingBoard"
      @create="createBoard"
    />
  </div>
</template>
